import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useScopedOrders } from './useScopedOrders';
import {
  buildOrderNotifications,
  getNotificationReadStorageKey,
  loadReadNotificationIds,
  saveReadNotificationIds,
  type HeaderNotification,
} from '../utils/notificationBuilders';

const MAX_DROPDOWN_ITEMS = 12;

export function useHeaderNotifications() {
  const { user, loading: authLoading } = useAuth();
  const { orders, loading: ordersLoading, error } = useScopedOrders({
    first: 100,
    paginateAll: true,
  });

  const storageKey = useMemo(
    () => getNotificationReadStorageKey(user?.databaseId || user?.id),
    [user?.databaseId, user?.id]
  );

  const [readIds, setReadIds] = useState<Set<string>>(() =>
    loadReadNotificationIds(storageKey)
  );

  useEffect(() => {
    setReadIds(loadReadNotificationIds(storageKey));
  }, [storageKey]);

  const notifications = useMemo(
    () => buildOrderNotifications(orders),
    [orders]
  );

  const unreadNotifications = useMemo(
    () => notifications.filter((item) => !readIds.has(item.id)),
    [notifications, readIds]
  );

  const dropdownNotifications = useMemo(
    () => unreadNotifications.slice(0, MAX_DROPDOWN_ITEMS),
    [unreadNotifications]
  );

  const unreadCount = unreadNotifications.length;

  const markAsRead = useCallback(
    (id: string) => {
      setReadIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        saveReadNotificationIds(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const markAllAsRead = useCallback(() => {
    setReadIds((prev) => {
      const next = new Set(prev);
      notifications.forEach((item) => next.add(item.id));
      saveReadNotificationIds(storageKey, next);
      return next;
    });
  }, [notifications, storageKey]);

  return {
    notifications: dropdownNotifications as HeaderNotification[],
    allNotifications: notifications,
    unreadCount,
    loading: authLoading || ordersLoading,
    error,
    markAsRead,
    markAllAsRead,
  };
}
